import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Input as UIInput } from '@utima/ui';
import { ListChecks, ListTodo, Search, Loader2 } from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';

import type { WebsiteRecordValue } from '@/store/slices/documentModalSlice';

import { WebsiteRecord } from './WebsiteRecord';

/**
 * Check if the url matches the search query.
 */
function isUrlMatch(url: string, search: string) {
  return url.toLowerCase().includes(search.toLowerCase());
}

export type WebsiteRecordsProps = {
  loading?: boolean;
  message?: string;
  failed?: boolean;
  stale?: boolean;
  webUrls: WebsiteRecordValue[];
  setWebUrls: (values: WebsiteRecordValue[]) => void;
  search: string;
  setSearch: (search: string) => void;
};

export const WebsiteRecords = memo(
  ({
    search,
    setSearch,
    webUrls,
    setWebUrls,
    loading = false,
    message,
    failed = false,
    stale = false,
  }: WebsiteRecordsProps) => {
    const { t } = useLingui();
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    useEffect(() => {
      setElapsedSeconds(0);
      if (!loading) return;
      const started = Date.now();
      const timer = setInterval(
        () => setElapsedSeconds(Math.floor((Date.now() - started) / 1000)),
        1000,
      );

      return () => clearInterval(timer);
    }, [loading]);
    const elapsed = `${Math.floor(elapsedSeconds / 60)}:${String(elapsedSeconds % 60).padStart(2, '0')}`;

    const [kinds, setKinds] = useState({ page: true, document: true });
    /**
     * Handles the (de)selection of all urls that match the search query.
     */
    const handleMultiple = useCallback(
      (selected: boolean) => {
        setWebUrls(
          webUrls.map(record => {
            if (
              kinds[record.kind ?? 'page'] &&
              isUrlMatch(record.url, search)
            ) {
              return {
                ...record,
                selected,
              };
            }

            return record;
          }),
        );
      },
      [kinds, search, setWebUrls, webUrls],
    );

    /**
     * Handle the check/uncheck of a website record checkbox.
     */
    const handleRecordChange = useCallback(
      (id: string, data: Partial<WebsiteRecordValue>) => {
        setWebUrls(
          webUrls.map(rec => {
            if (rec.id === id) {
              return {
                ...rec,
                ...data,
              };
            }

            return rec;
          }),
        );
      },
      [webUrls, setWebUrls],
    );

    const filteredUrls = useMemo(() => {
      return webUrls.filter(
        val => kinds[val.kind ?? 'page'] && isUrlMatch(val.url, search),
      );
    }, [webUrls, search, kinds]);

    const selectedCount = useMemo(() => {
      return webUrls.filter(val => val.selected).length;
    }, [webUrls]);

    return (
      <div className='flex shrink-0 flex-col gap-2 overflow-hidden rounded-lg border border-border p-2'>
        <div className='flex flex-wrap items-center gap-x-4 gap-y-2'>
          <div className='min-w-[140px] flex-1'>
            <UIInput
              name='search'
              placeholder={t`Filter`}
              value={search}
              onChange={e => setSearch(e.target.value)}
              addonBefore={<Search className='size-4' />}
            />
          </div>
          <div className='flex flex-wrap items-center gap-3 text-xs'>
            <label className='flex cursor-pointer items-center gap-1.5'>
              <input
                type='checkbox'
                className='size-4 accent-primary'
                checked={kinds.page}
                onChange={event =>
                  setKinds(current => ({
                    ...current,
                    page: event.target.checked,
                  }))
                }
              />
              <Trans>Pages</Trans> (
              {
                webUrls.filter(value => (value.kind ?? 'page') === 'page')
                  .length
              }
              )
            </label>
            <label className='flex cursor-pointer items-center gap-1.5'>
              <input
                type='checkbox'
                className='size-4 accent-primary'
                checked={kinds.document}
                onChange={event =>
                  setKinds(current => ({
                    ...current,
                    document: event.target.checked,
                  }))
                }
              />
              <Trans>Documents</Trans> (
              {webUrls.filter(value => value.kind === 'document').length})
            </label>
          </div>
        </div>

        <div className='min-h-0 grow overflow-hidden rounded-lg border border-border bg-white'>
          <div className='flex size-full flex-col text-xs'>
            <div className='flex w-full items-center justify-between border-b border-border px-3 py-2 font-bold text-gray-600'>
              <div>
                <Trans>Resource URL</Trans>
              </div>
              <div>
                <Trans>Information importance</Trans>
              </div>
            </div>

            <div className='relative min-h-[200px] flex-1' aria-busy={loading}>
              {loading ? (
                <div
                  role='status'
                  className='flex h-[240px] flex-col items-center justify-center gap-2 text-sm text-muted-fg'
                >
                  <span className='text-xs tabular-nums' aria-live='off'>
                    <Trans>Elapsed: {elapsed}</Trans>
                  </span>
                  <Loader2 className='size-4 animate-spin' />
                  <Trans>Searching for sources…</Trans>
                </div>
              ) : failed ? (
                <div
                  role='alert'
                  className='flex min-h-[200px] items-center justify-center p-4 text-center text-sm text-danger'
                >
                  {message}
                </div>
              ) : filteredUrls.length === 0 ? (
                <div className='flex min-h-[200px] items-center justify-center p-4 text-sm text-muted-fg'>
                  {webUrls.length ? (
                    <Trans>No matching sources.</Trans>
                  ) : (
                    <Trans>
                      Enter a URL and search to find pages and documents.
                    </Trans>
                  )}
                </div>
              ) : (
                <div className='h-[280px] shrink-0'>
                  <AutoSizer>
                    {({ height, width }) => (
                      <FixedSizeList
                        height={height}
                        width={width}
                        itemCount={filteredUrls.length}
                        itemSize={28}
                        className='overflow-x-hidden'
                      >
                        {({ index, style }) => (
                          <WebsiteRecord
                            key={filteredUrls[index].id}
                            id={filteredUrls[index].id}
                            description={filteredUrls[index].url}
                            weight={filteredUrls[index].weight}
                            checked={filteredUrls[index].selected}
                            style={style}
                            onRecordChange={handleRecordChange}
                          />
                        )}
                      </FixedSizeList>
                    )}
                  </AutoSizer>
                </div>
              )}
            </div>
          </div>
        </div>

        {!loading && !failed && (stale || message) && (
          <p role='status' className='px-1 text-xs text-muted-fg'>
            {stale ? (
              <Trans>Settings changed. Search again before adding.</Trans>
            ) : (
              message
            )}
          </p>
        )}
        <div className='flex flex-wrap items-center justify-between gap-2 pt-1'>
          <div className='flex flex-wrap items-center gap-1'>
            <Button
              outline
              variant='ghost'
              data-cta='admin-website-records-select-all'
              size='xs'
              disabled={loading || failed || !filteredUrls.length}
              onClick={() => handleMultiple(true)}
            >
              <ListChecks className='size-4' />
              <Trans>Select all</Trans>
            </Button>
            <Button
              outline
              variant='ghost'
              data-cta='admin-website-records-deselect-all'
              size='xs'
              disabled={loading || failed || !filteredUrls.length}
              onClick={() => handleMultiple(false)}
            >
              <ListTodo className='size-4' />
              <Trans>Deselect all</Trans>
            </Button>
          </div>
          <div className='text-sm font-bold text-gray-600'>
            {selectedCount}/{webUrls.length}
          </div>
        </div>
      </div>
    );
  },
);
